import { useEffect, useRef } from "react";
import * as d3 from "d3";

export default function RelationshipGraph({ nodes = [], links = [] }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || nodes.length === 0) return;
    const container = ref.current;
    // Safe DOM clear (no innerHTML assignment, no XSS surface even with empty string).
    while (container.firstChild) container.removeChild(container.firstChild);
    const width = container.clientWidth;
    const height = 500;

    const svg = d3.select(container).append("svg")
      .attr("width", width).attr("height", height)
      .attr("viewBox", [-width/2, -height/2, width, height]);

    const colorMap = {
      Regulator: "hsl(var(--info))",
      Ministry: "hsl(var(--info))",
      Bank: "hsl(var(--success))",
      DFI: "hsl(var(--success))",
      Investor: "hsl(var(--warning))",
      Association: "hsl(var(--muted-foreground))",
      Accelerator: "hsl(var(--muted-foreground))",
      Partner: "hsl(var(--foreground))",
      Company: "hsl(var(--foreground))",
    };

    const nodesCopy = nodes.map((n) => ({ ...n }));
    const linksCopy = links.map((l) => ({ ...l }));

    const sim = d3.forceSimulation(nodesCopy)
      .force("link", d3.forceLink(linksCopy).id((d) => d.id).distance(100).strength(0.3))
      .force("charge", d3.forceManyBody().strength(-220))
      .force("center", d3.forceCenter(0, 0))
      .force("collision", d3.forceCollide().radius(28));

    const link = svg.append("g")
      .attr("stroke", "hsl(var(--border))").attr("stroke-width", 1)
      .selectAll("line").data(linksCopy).join("line");

    const node = svg.append("g")
      .selectAll("g").data(nodesCopy).join("g")
      .call(d3.drag()
        .on("start", (e, d) => { if (!e.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
        .on("drag",  (e, d) => { d.fx = e.x; d.fy = e.y; })
        .on("end",   (e, d) => { if (!e.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      );

    node.append("circle")
      .attr("r", 8).attr("fill", (d) => colorMap[d.category] || "hsl(var(--muted-foreground))")
      .attr("stroke", "hsl(var(--background))").attr("stroke-width", 2);

    node.append("text")
      .attr("x", 12).attr("y", 4)
      .attr("font-size", 11)
      .attr("fill", "hsl(var(--foreground))")
      .attr("font-family", "IBM Plex Sans")
      .text((d) => d.label);

    node.append("title").text((d) => `${d.label} · ${d.category}`);

    sim.on("tick", () => {
      link
        .attr("x1", (d) => d.source.x).attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x).attr("y2", (d) => d.target.y);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => sim.stop();
  }, [nodes, links]);

  if (!nodes || nodes.length === 0) {
    return <div className="text-sm text-muted-foreground p-8 text-center border border-dashed border-border rounded-2xl bg-card" data-testid="graph-empty">No graph data yet.</div>;
  }
  return <div ref={ref} className="w-full bg-card border border-border rounded-2xl overflow-hidden card-elev" data-testid="relationship-graph" />;
}
